import {
  type ActivePlanKey,
  comparePlans,
} from '@sparklayer-io/spark-dash-core/constants/features';
import { Text } from '@ui/themed';
import { t } from 'i18next';
import type { ReactNode } from 'react';

/** Separators for the plan list, so the conjunction stays correct in every locale. */
const planListFormatter = new Intl.ListFormat(undefined, { style: 'long', type: 'conjunction' });

/**
 * Chip gradient per plan tier, based on BillingPlanGradients.
 */
const PLAN_CHIP_GRADIENTS: Partial<Record<ActivePlanKey, string>> = {
  v2_basic: 'from-[#ffb87a] to-[#ff9443]',
  v2_starter_plus: 'from-[#ffb87a] to-[#ff9443]',
  v2_growth: 'from-[#66ffb8] to-[#088012]',
  v2_pro: 'from-gray-700 to-black',
  enterprise: 'from-[#b318ff] to-[#1e43ff]',
  enterprise_onboarding: 'from-[#b318ff] to-[#1e43ff]',
};
const DEFAULT_CHIP_GRADIENT = 'from-gray-500 to-gray-900';

/** Highlights a single plan name inside the availability list, tinted by plan tier. */
function PlanChip({ plan, children }: { plan: ActivePlanKey; children?: ReactNode }) {
  const gradient = PLAN_CHIP_GRADIENTS[plan] ?? DEFAULT_CHIP_GRADIENT;
  return (
    <span
      className={`inline-block rounded bg-linear-to-r ${gradient} px-1 py-px align-middle text-[10px] text-white`}
    >
      {children}
    </span>
  );
}

/**
 * "Available on: Starter, Growth, and Pro" — every plan gets its own chip, cheapest first
 * whatever order the caller passed. Plans that share a display name (`v2_basic` and
 * `v2_starter_plus` are both "Starter") collapse into a single chip.
 */
export function PlanAvailabilityBadge({ plans }: { plans: ActivePlanKey[] }) {
  const planByName = new Map<string, ActivePlanKey>();
  for (const plan of [...plans].sort(comparePlans)) {
    const name = t(`labels:plans.${plan}`);
    if (!planByName.has(name)) planByName.set(name, plan);
  }
  const names = [...planByName.keys()];

  return (
    <span className="inline-flex items-center rounded-full border border-spark-border-default bg-gray-50 px-3 py-1">
      <Text variant="secondary">
        {t('labels:available_on')}{' '}
        {planListFormatter.formatToParts(names).map((part, index) =>
          part.type === 'element' ? (
            // biome-ignore lint/style/noNonNullAssertion: part.value is always a name we just inserted into planByName
            <PlanChip key={part.value} plan={planByName.get(part.value)!}>
              {part.value}
            </PlanChip>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: formatted separators are static text in a fixed order
            <span key={`separator-${index}`}>{part.value}</span>
          ),
        )}
      </Text>
    </span>
  );
}
